import { Chip, SxProps } from "@mui/material";
import {
  SubscriptionStatus,
  SubscriptionStatusInput,
  getGraceDaysLeft,
  getSubscriptionStatus,
} from "src/business/subscription/subscriptionStatus";
import {
  colorBorderSuccess,
  colorBorderWarning,
  colorBorderError,
  colorChipBackground,
  colorChipBorder,
  colorSurfaceSuccess,
  colorSurfaceWarning,
  colorSurfaceError,
  colorTextMuted,
  colorTextSuccess,
  colorTextWarning,
  colorTextError,
  radiusChip,
} from "src/design/tokens";

const STATUS_STYLES: Record<
  SubscriptionStatus,
  { bg: string; color: string; border: string; label: string }
> = {
  [SubscriptionStatus.Active]: {
    bg: colorSurfaceSuccess,
    color: colorTextSuccess,
    border: colorBorderSuccess,
    label: "Active",
  },
  [SubscriptionStatus.Grace]: {
    bg: colorSurfaceWarning,
    color: colorTextWarning,
    border: colorBorderWarning,
    label: "Grace period",
  },
  [SubscriptionStatus.Expired]: {
    bg: colorSurfaceError,
    color: colorTextError,
    border: colorBorderError,
    label: "Expired",
  },
  [SubscriptionStatus.Perpetual]: {
    bg: colorChipBackground,
    color: colorTextMuted,
    border: colorChipBorder,
    label: "Never expires",
  },
};

interface SubscriptionStatusChipProps {
  subscription: SubscriptionStatusInput;
  sx?: SxProps;
}

/**
 * The derived subscription status as a chip. In the grace period it also counts
 * down the days of access left before the subscription is revoked.
 */
export const SubscriptionStatusChip = ({
  subscription,
  sx,
}: SubscriptionStatusChipProps) => {
  const status = getSubscriptionStatus(subscription);
  const { bg, color, border, label } = STATUS_STYLES[status];

  const daysLeft =
    status === SubscriptionStatus.Grace && subscription.nextBillingCycle
      ? getGraceDaysLeft(subscription.nextBillingCycle)
      : null;

  return (
    <Chip
      size="small"
      label={daysLeft === null ? label : `${label} · ${daysLeft}d left`}
      sx={{
        backgroundColor: bg,
        color,
        border: `1px solid ${border}`,
        borderRadius: radiusChip,
        fontWeight: 600,
        fontSize: 11,
        ...sx,
      }}
    />
  );
};
